// =====================================================================
// NIKAO CUP 2026
//
// 32 players - 4 divisions (Nicklaus, Woods, Palmer, Hogan) of 8.
// 7 regular season dates = a full round robin inside each division.
// Top 4 per division reach a 16-player single-elimination playoff.
//
// Points:      Win 5 / Tie 3 / Loss 1 / Forfeit 0 (opponent gets 3)
// Tiebreakers: head-to-head -> most wins -> win+tie points -> chip-off
// Strokes:     9-hole WHS course handicaps (tee-box rating adjustment
//              included, so cross-tee matches are fair), then the league
//              rules: index capped at 18.0, 85% allowance, max 8 strokes.
// Rules:       see components/RulesTab.js for the official format.
// Access:      public read-only; shared password unlocks editing.
// =====================================================================

import React, { useCallback, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import {
  Calendar as CalendarIcon, ClipboardList, BarChart3, Users,
  CalendarCog, MapPin, Trophy, AlertTriangle, ScrollText, Medal,
} from 'lucide-react';

import { useGolfData } from './hooks/useGolfData';
import { useAdmin } from './hooks/useAdmin';

import AdminGate from './components/AdminGate';
import TeeSheet from './components/TeeSheet';
import ResultsList from './components/ResultsList';
import Leaderboard from './components/Leaderboard';
import RosterTab from './components/RosterTab';
import ScheduleTab from './components/ScheduleTab';
import CoursesTab from './components/CoursesTab';
import PlayoffsTab from './components/PlayoffsTab';
import RulesTab from './components/RulesTab';
import PastWinnersTab from './components/PastWinnersTab';

import { computeStandings, mirrorResult, substituteInMatch } from './utils/scoring';
import { generateRegularSeason } from './utils/scheduling';
import { buildBracket, advanceRound } from './utils/playoffs';
import { db } from './lib/database';
import { DIVISIONS, SEASON } from './utils/constants';
import { uid } from './utils/helpers';

export default function App() {
  const data = useGolfData();
  const {
    golfers, matches, courses, teeSets, seasonDates, tiebreaks,
    loading, error, refreshData,
  } = data;
  const admin = useAdmin();

  const activeGolfers = useMemo(
    () => (golfers || []).filter(g => g.active !== false), [golfers]);

  const standings = useMemo(
    () => computeStandings(golfers || [], matches || [], tiebreaks || []),
    [golfers, matches, tiebreaks]);

  const golfersByDivision = useMemo(() => {
    const m = {};
    DIVISIONS.forEach(d => { m[d] = activeGolfers.filter(g => g.division === d).map(g => g.id); });
    return m;
  }, [activeGolfers]);

  // -------------------------------------------------------------------
  // Guard: every mutation goes through this so a non-admin cannot write
  // even if a control is somehow reachable.
  // -------------------------------------------------------------------
  const requireAdmin = useCallback((fn) => async (...args) => {
    if (!admin.isAdmin) {
      alert('Unlock admin to make changes.');
      return;
    }
    try {
      return await fn(...args);
    } catch (e) {
      console.error(e);
      alert(`That did not save: ${e.message || e}`);
    }
  }, [admin.isAdmin]);

  // ---- Golfers ----
  const saveGolfer = requireAdmin(async (g) => {
    await db.golfers.upsert(g);
    await refreshData();
  });

  const deleteGolfer = requireAdmin(async (id) => {
    // Soft delete: keeps the player's completed results readable.
    const g = (golfers || []).find(x => x.id === id);
    if (g) await db.golfers.upsert({ ...g, active: false });
    await refreshData();
  });

  // Season-long substitution: the incoming player takes over the outgoing
  // player's slot in every match that has NOT been played yet.
  const replacePlayer = requireAdmin(async (outgoing, incoming) => {
    await db.golfers.upsert(incoming);
    await db.golfers.upsert({ ...outgoing, active: false, replaced_by: incoming.id });

    const updates = [];
    for (const m of (matches || [])) {
      const swapped = substituteInMatch(m, outgoing.id, incoming.id);
      if (swapped) updates.push(stripTimestamps(swapped));
    }

    if (updates.length) await db.matches.upsertMany(updates);
    await refreshData();
    alert(
      `${incoming.name} replaces ${outgoing.name} in ${updates.length} unplayed match` +
      `${updates.length === 1 ? '' : 'es'}. Completed results stay with ${outgoing.name}.`
    );
  });

  const swapDivisions = requireAdmin(async (a, b) => {
    await db.golfers.upsert({ ...stripTimestamps(a), division: b.division });
    await db.golfers.upsert({ ...stripTimestamps(b), division: a.division });
    await refreshData();
    alert('Divisions swapped. Regenerate the schedule from the Schedule tab so the round robin matches the new rosters.');
  });

  // ---- Courses & tees ----
  const saveCourse   = requireAdmin(async (c) => { await db.courses.upsert(c); await refreshData(); });
  const deleteCourse = requireAdmin(async (id) => {
    if (!window.confirm('Delete this course and its tee sets?\n\nThis will fail if any match is still scheduled here - delete or move those matches first.')) return;
    await db.courses.remove(id); await refreshData();
  });
  const saveTeeSet   = requireAdmin(async (t) => { await db.teeSets.upsert(t); await refreshData(); });
  const deleteTeeSet = requireAdmin(async (id) => { await db.teeSets.remove(id); await refreshData(); });

  // ---- Schedule ----
  const saveDate = requireAdmin(async (d) => { await db.seasonDates.upsert(d); await refreshData(); });
  const deleteDate = requireAdmin(async (id) => { await db.seasonDates.remove(id); await refreshData(); });
  const saveAssignment = requireAdmin(async (a) => { await db.assignments.upsert(a); await refreshData(); });
  const deleteAssignment = requireAdmin(async (id) => { await db.assignments.remove(id); await refreshData(); });

  const savePairings = requireAdmin(async (date, pairs) => {
    await db.pairings.removeWhere('season_date_id', date.id);
    const rows = (pairs || []).map((p, i) => ({
      id: `${date.id}_p${i + 1}`,
      season_date_id: date.id,
      division_a: p[0],
      division_b: p[1],
    }));
    if (rows.length) await db.pairings.upsertMany(rows);
    await refreshData();
  });

  const generateSeason = requireAdmin(async () => {
    const { matches: generated, warnings } = generateRegularSeason({
      seasonDates, golfersByDivision, golfers: activeGolfers,
    });
    if (generated.length === 0) {
      alert('Nothing was generated.\n\n' + (warnings.join('\n') || 'Check the roster and dates.'));
      return;
    }
    await db.clearRegularSeasonMatches();
    await db.matches.upsertMany(generated);
    await refreshData();
    alert(
      `Generated ${generated.length} matches across ${seasonDates.filter(d => d.phase === 'regular').length} dates.` +
      (warnings.length ? `\n\nWarnings:\n${warnings.join('\n')}` : '')
    );
  });

  const clearSeason = requireAdmin(async () => {
    await db.clearRegularSeasonMatches();
    await refreshData();
  });

  // ---- Results ----
  // Setting one player's result writes the opponent's mirror result too, so
  // it is impossible to record two winners in one match.
  const changeResult = requireAdmin(async (match, golferId, opponentId, value) => {
    const results = mirrorResult(match.results, golferId, opponentId, value);
    await db.matches.upsert({ ...stripTimestamps(match), results });
    await refreshData();
  });

  const changeTee = requireAdmin(async (match, golferId, teeSetId) => {
    const selections = { ...(match.golfer_selections || {}) };
    selections[golferId] = { ...(selections[golferId] || {}), tee_set_id: teeSetId };
    await db.matches.upsert({ ...stripTimestamps(match), golfer_selections: selections });
    await refreshData();
  });

  const saveMatch = requireAdmin(async (m) => {
    await db.matches.upsert(stripTimestamps(m));
    await refreshData();
  });

  // ---- Tiebreaks ----
  const resolveTiebreak = requireAdmin(async (division, winner, loser, method) => {
    await db.tiebreaks.upsert({
      id: uid('tb'),
      division,
      winner_id: winner.id,
      loser_id: loser.id,
      method,
    });
    await refreshData();
  });

  // ---- Playoffs ----
  const buildBracketAction = requireAdmin(async (matchups) => {
    const { matches: bracket, warnings } = buildBracket(matchups, seasonDates);
    if (bracket.length === 0) {
      alert('Could not build the bracket.\n\n' + warnings.join('\n'));
      return;
    }
    await db.clearPlayoffMatches();
    await db.matches.upsertMany(bracket);
    await refreshData();
    if (warnings.length) alert(`Bracket built with warnings:\n${warnings.join('\n')}`);
  });

  const advanceRoundAction = requireAdmin(async (round) => {
    const playoffMatches = (matches || []).filter(m => m.phase === 'playoff');
    const { updated, note } = advanceRound(playoffMatches, round);
    if (note) { alert(note); return; }
    if (!updated.length) { alert('Nothing to advance.'); return; }
    await db.matches.upsertMany(updated.map(stripTimestamps));
    await refreshData();
  });

  const clearBracket = requireAdmin(async () => {
    await db.clearPlayoffMatches();
    await refreshData();
  });

  // -------------------------------------------------------------------
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-emerald-600" />
          <p className="text-gray-600">Loading league data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="max-w-lg rounded-lg border border-red-200 bg-white p-6">
          <div className="mb-2 flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />
            <h1 className="text-lg font-semibold">Could not load the league</h1>
          </div>
          <p className="text-sm text-gray-700">{error}</p>
          <ol className="mt-3 list-inside list-decimal space-y-1 text-sm text-gray-600">
            <li>Create a new Supabase project for 2026.</li>
            <li>Run <code>sql/01-schema.sql</code> then <code>sql/02-seed.sql</code> in its SQL editor.</li>
            <li>Copy <code>.env.example</code> to <code>.env</code> and fill in the URL, anon key and admin password.</li>
            <li>Restart <code>npm start</code>.</li>
          </ol>
        </div>
      </div>
    );
  }

  const regularCount = (matches || []).filter(m => (m.phase || 'regular') === 'regular').length;
  const playoffCount = (matches || []).filter(m => m.phase === 'playoff').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto max-w-7xl px-3 py-4 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-600 text-lg font-bold text-white">
                <Trophy className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
                  NIKAO CUP {SEASON}
                </h1>
                <p className="text-sm text-gray-600">
                  Nicklaus &middot; Woods &middot; Palmer &middot; Hogan
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right text-xs text-gray-500">
                <div>{activeGolfers.length} players &middot; {regularCount} matches &middot; {playoffCount} playoff</div>
                <div>{new Date().toLocaleDateString()}</div>
              </div>
              <AdminGate admin={admin} />
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6">
        <Tabs defaultValue="teesheet">
          <TabsList className="grid h-auto w-full grid-cols-3 gap-1 sm:grid-cols-5 lg:grid-cols-9">
            <TabsTrigger value="teesheet" className="px-1 py-2 text-xs sm:text-sm">
              <CalendarIcon className="mr-1 hidden h-4 w-4 lg:inline" />Tee Sheet
            </TabsTrigger>
            <TabsTrigger value="results" className="px-1 py-2 text-xs sm:text-sm">
              <ClipboardList className="mr-1 hidden h-4 w-4 lg:inline" />Results
            </TabsTrigger>
            <TabsTrigger value="standings" className="px-1 py-2 text-xs sm:text-sm">
              <BarChart3 className="mr-1 hidden h-4 w-4 lg:inline" />Standings
            </TabsTrigger>
            <TabsTrigger value="playoffs" className="px-1 py-2 text-xs sm:text-sm">
              <Trophy className="mr-1 hidden h-4 w-4 lg:inline" />Playoffs
            </TabsTrigger>
            <TabsTrigger value="rules" className="px-1 py-2 text-xs sm:text-sm">
              <ScrollText className="mr-1 hidden h-4 w-4 lg:inline" />Rules
            </TabsTrigger>
            <TabsTrigger value="winners" className="px-1 py-2 text-xs sm:text-sm">
              <Medal className="mr-1 hidden h-4 w-4 lg:inline" />Winners
            </TabsTrigger>
            <TabsTrigger value="roster" className="px-1 py-2 text-xs sm:text-sm">
              <Users className="mr-1 hidden h-4 w-4 lg:inline" />Roster
            </TabsTrigger>
            <TabsTrigger value="schedule" className="px-1 py-2 text-xs sm:text-sm">
              <CalendarCog className="mr-1 hidden h-4 w-4 lg:inline" />Schedule
            </TabsTrigger>
            <TabsTrigger value="courses" className="px-1 py-2 text-xs sm:text-sm">
              <MapPin className="mr-1 hidden h-4 w-4 lg:inline" />Courses
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teesheet">
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><CalendarIcon className="h-5 w-5" />Tee Sheet</CardTitle>
                <CardDescription>
                  Four players per tee time, two head-to-head matches per group. Strokes shown are
                  9-hole course handicap differences, adjusted for each player's tee box.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <TeeSheet
                  matches={matches} golfers={golfers} teeSets={teeSets} courses={courses}
                  seasonDates={seasonDates} isAdmin={admin.isAdmin} onChangeTee={changeTee}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="results">
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><ClipboardList className="h-5 w-5" />Enter Results</CardTitle>
                <CardDescription>Win 5 &middot; Tie 3 &middot; Loss 1 &middot; Forfeit 0 (opponent receives 3).</CardDescription>
              </CardHeader>
              <CardContent>
                <ResultsList
                  matches={matches} golfers={golfers} teeSets={teeSets} courses={courses}
                  seasonDates={seasonDates} isAdmin={admin.isAdmin}
                  onChangeResult={changeResult} onChangeTee={changeTee}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="standings">
            <div className="mt-4">
              <Leaderboard
                standings={standings} isAdmin={admin.isAdmin}
                onResolveTiebreak={resolveTiebreak} tiebreaks={tiebreaks}
              />
            </div>
          </TabsContent>

          <TabsContent value="playoffs">
            <PlayoffsTab
              standings={standings} matches={matches} golfers={golfers}
              teeSets={teeSets} courses={courses} seasonDates={seasonDates}
              isAdmin={admin.isAdmin}
              onBuildBracket={buildBracketAction}
              onSaveMatch={saveMatch}
              onAdvanceRound={advanceRoundAction}
              onClearBracket={clearBracket}
              onChangeResult={changeResult}
              onChangeTee={changeTee}
            />
          </TabsContent>

          <TabsContent value="rules">
            <RulesTab />
          </TabsContent>

          <TabsContent value="winners">
            <PastWinnersTab />
          </TabsContent>

          <TabsContent value="roster">
            <RosterTab
              golfers={golfers} teeSets={teeSets} courses={courses} matches={matches}
              isAdmin={admin.isAdmin}
              onSaveGolfer={saveGolfer} onDeleteGolfer={deleteGolfer}
              onReplacePlayer={replacePlayer} onSwapDivisions={swapDivisions}
            />
          </TabsContent>

          <TabsContent value="schedule">
            <ScheduleTab
              seasonDates={seasonDates} courses={courses} golfers={golfers} matches={matches}
              isAdmin={admin.isAdmin}
              onSaveDate={saveDate} onDeleteDate={deleteDate}
              onSaveAssignment={saveAssignment} onDeleteAssignment={deleteAssignment}
              onSavePairings={savePairings}
              onGenerateSeason={generateSeason} onClearSeason={clearSeason}
            />
          </TabsContent>

          <TabsContent value="courses">
            <CoursesTab
              courses={courses} teeSets={teeSets} isAdmin={admin.isAdmin}
              onSaveCourse={saveCourse} onDeleteCourse={deleteCourse}
              onSaveTeeSet={saveTeeSet} onDeleteTeeSet={deleteTeeSet}
              onSyncGhin={() => alert('GHIN sync is switched off. Set GHIN_API_ENABLED = true in src/utils/constants.js to enable it.')}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

// Postgres manages created_at / updated_at; sending them back causes
// trigger conflicts on upsert.
function stripTimestamps(row) {
  const { created_at, updated_at, ...rest } = row || {};
  return rest;
}
